import styled, { css, keyframes } from 'styled-components';
import {
  GTM_DATA_ATTRIBUTE,
  DESKTOP_MIN_BREAK,
  MOBILE_MAX_BREAK,
  SYSTEM_ALERT_BANNER_HEIGHT,
  NAV_OFFSET_DETAIL_VIEW,
  NAV_HEIGHT,
} from '#/shared/constants';
import { ClearButton } from '#/shared/components/Styled';
import { focusCssRule } from '#/shared/css/focus';
import BaseArrowLeftIcon from '#/shared/components/GeneratedIcons/generic/ArrowLeft';
import BaseStarIcon from '#/shared/components/GeneratedIcons/generic/Star';
import CloseBase from '#/shared/components/GeneratedIcons/generic/Close';
import BaseTimestampRefreshButton from '#/shared/components/TimestampRefreshButton';
import { CurrentLocationMarker as BaseCurrentLocationMarker } from '#/map/components/CurrentLocationOverlay';

export const TripToolWrapper = styled.div`
  box-shadow: ${props => props.theme.boxShadowPanel};
  clip-path: inset(0px -10px 0px -10px);
  visibility: ${({ minimized }) => (minimized ? 'hidden' : 'visible')};
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.backgroundColor};

  @media (max-width: ${MOBILE_MAX_BREAK}) {
    flex: 1;
  }

  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    overflow: hidden;
    height: 100%;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #cfd8dc #f5f5f5;
    &::-webkit-scrollbar-track {
      background: #cfd8dc;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #f5f5f5;
    }
  }
`;

/** This extra wrapper ensures that this component sizes and
 * styles correctly regardless of how many extra divs reach
 * router may wrap around it
 */
export const StopsWrapper = styled.div.attrs(props => ({
  [GTM_DATA_ATTRIBUTE]: props.dataGtm,
}))`
  display: flex;
  flex: 1;
  flex-direction: column;
  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    overflow-y: auto;
  }
`;

export const HeaderAndToolsWrapper = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  background-color: transparent;
  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    height: calc(
      100vh - ${NAV_HEIGHT} - ${NAV_OFFSET_DETAIL_VIEW} -
        ${({ alertBannerIsVisible }) =>
          alertBannerIsVisible ? SYSTEM_ALERT_BANNER_HEIGHT : '0px'}
    );
  }
`;

export const SidePanelChevronIcon = styled(BaseArrowLeftIcon).attrs(() => ({
  width: 16,
  height: 16,
}))`
  fill: ${props => (props.$showChevron ? props.theme.tabColor : 'transparent')};
  transform: rotate(
    ${props => (props.$sidePanelIsMinimized ? '0.25turn' : '0.75turn')}
  );
`;

const fadeIn = keyframes`
  from {
	transform: translateY(10px);
	opacity: 0;
  }
  to {
	transform: translateY(0);
	opacity: 1;
  }`;

const fadeOut = keyframes`
  from {
	transform: translateY(0);
	opacity:1;
  }
  to {
	transform: translateY(10px);
	opacity: 0;
  }`;

export const MinMaxSidePanelButtonWrapper = styled.div`
  display: flex;
  flex: 1 0 auto;
  position: absolute;
  top: -30px;
  align-items: center;
  justify-content: center;
  align-self: center;
  opacity: ${props => (props.showMinimizeToggle ? 1 : 0)};
  animation: ${props => {
      if (!props.mountMinimizeToggle) {
        return 'none';
      }
      if (props.showMinimizeToggle) {
        return fadeIn;
      }
      return fadeOut;
    }}
    0.25s ease-in-out;
`;

export const MinMaxSidePanelButton = styled(ClearButton)`
  line-height: 0.5;
  padding: 7px 6px 5px;
  background-color: ${props => props.theme.mainColor};
  border-radius: 100%;
  overflow: hidden;
  @media (any-hover: hover) {
    &:hover {
      background-color: ${props => props.theme.inverseBackgroundColor4};
      ${SidePanelChevronIcon} {
        fill: ${props => props.theme.tabActiveColor};
      }
    }
  }
  &:focus,
  &:active {
    background-color: ${props => props.theme.inverseBackgroundColor4};
  }
`;

const BaseButton = styled(ClearButton)`
  display: flex;
  flex: 0;
  align-items: center;
  padding: 0;
  border-radius: ${props => props.theme.buttonRadius};

  &:focus {
    background-color: transparent;
  }

  ${focusCssRule({ type: 'inset' })}

  @media (any-hover: hover) {
    &:hover {
      background-color: ${props => props.theme.activeColor};
    }
  }
`;

export const ClearCurrentLocationIcon = styled(CloseBase).attrs(props => ({
  fill: props.theme.secondaryFontColor,
  width: 19,
  height: 19,
}))`
  display: flex;
  opacity: ${props => props.theme.initialButtonOpacity};
`;

export const ClearCurrentLocationButton = styled(BaseButton)`
  align-items: center;
  justify-content: center;
  font-size: 1em;
  @media (any-hover: hover) {
    &:hover {
      background-color: transparent;
      ${ClearCurrentLocationIcon} {
        opacity: 1;
      }
    }
  }
`;

export const CurrentLocationButton = styled(BaseButton)`
  flex: 0 1 auto;
  min-width: 0;
  font-size: 1em;
  font-weight: bold;
  color: ${props => props.theme.secondaryFontColor};
  margin: 0 0 0 7px;
  padding: 5px;
`;

export const HeaderText = styled.div`
  margin-left: 7px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const headerMarginStyle = css`
  margin: 8px 0 0 2px;
`;

export const CurrentLocationHeaderWrapper = styled.div`
  display: flex;
  ${headerMarginStyle}
`;

export const FavoritesContainer = styled.div`
  font-size: 1em;
  font-weight: bold;
  color: ${props => props.theme.secondaryFontColor};
  display: flex;
  align-items: center;
  padding: 5px 10px;
  ${headerMarginStyle}
`;

export const FavoriteStar = styled(BaseStarIcon).attrs({
  width: 20,
  height: 20,
})`
  fill: ${props => props.theme.trimetOrange};
`;

export const CurrenLocationMarkerWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 2px 0 0 0;

  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    margin: 2px 0 0 0;
  }
`;

export const ArrivalsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin: 6px 2px 0 2px;
`;

export const TimestampRefreshButton = styled(BaseTimestampRefreshButton)`
  position: absolute;
  right: 0;
`;

export const CurrentLocationMarker = styled(BaseCurrentLocationMarker).attrs({
  size: 11,
})``;
